import { Address } from '@models/address';
import {
	ClientType,
	EmploymentStatus,
	EmploymentStatusData,
	RepresentationType,
} from '@models/clients';
import {
	BusinessMatterClientWithAssociations,
	PersonMatterClientWithAssociations,
	SpousalStatus,
} from '@models/matter';

export function clientsWithAssociations(
	clients: PersonMatterClientWithAssociations[],
	associationClients: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[],
): PersonMatterClientWithAssociations[] {
	const personClients = clients.filter((client) => {
		const isPerson = client.type === ClientType.PERSON;
		const isConsentingSpouse =
			client?.fla?.spousalStatus === SpousalStatus.CONSENTING;
		const isRepresentationNone =
			client?.representation === RepresentationType.NONE;

		return isPerson && !isConsentingSpouse && isRepresentationNone;
	});

	const clientsAssociations = associationClients
		.filter((association) => association.clientAssociations?.length > 0)
		.map((clientAssociation) => clientAssociation?.clientAssociations)
		.flat()
		.map((x) => {
			return x.client as PersonMatterClientWithAssociations;
		});

	const noDuplicateAssociations = clientsAssociations.filter((association) => {
		return !personClients.some((client) => client.id === association.id);
	});

	return personClients.concat(noDuplicateAssociations);
}

export function buildEmploymentStatusData(
	currentData: EmploymentStatusData,
	field: string,
	value: string | EmploymentStatus | Address,
): EmploymentStatusData | null {
	let payload: EmploymentStatusData | null = {
		employmentStatus: null,
		employmentOccupation: null,
		employmentCompany: null,
		employmentAddress: null,
		employmentOther: null,
	};

	if (currentData) {
		payload = { ...currentData };
	}

	if (payload && field === 'employmentStatus') {
		payload.employmentStatus = value as EmploymentStatus;

		if (value !== EmploymentStatus.EMPLOYED) {
			payload.employmentAddress = null;
			payload.employmentOccupation = null;
			payload.employmentCompany = null;
		}

		if (value !== EmploymentStatus.OTHER) {
			payload.employmentOther = null;
		}

		if (!payload.employmentStatus || payload.employmentStatus === null) {
			payload = null;
		}

		if (
			value === EmploymentStatus.EMPLOYED &&
			payload?.employmentAddress === null
		) {
			payload.employmentAddress = {
				street: '',
				unit: '',
				poBox: '',
				city: '',
				postcode: '',
				province: '',
				country: '',
			};
		}
	}

	if (payload && field === 'employmentOccupation') {
		payload.employmentOccupation = value as string;
	}

	if (payload && field === 'employmentCompany') {
		payload.employmentCompany = value as string;
	}

	if (payload && field === 'employmentAddress') {
		payload.employmentAddress = value as Address;
	}

	if (payload && field === 'employmentOther') {
		payload.employmentOther = value as string;
	}

	return payload;
}

export function buildEmploymentAddressData(
	currentAddress: Address,
	field: string,
	value: string,
): Address | null {
	let payload: Address = {
		street: '',
		unit: '',
		poBox: '',
		city: '',
		postcode: '',
		province: '',
		country: '',
	};

	if (currentAddress) {
		payload = { ...currentAddress };
	}

	payload[
		field as keyof Pick<
			Address,
			'street' | 'unit' | 'poBox' | 'city' | 'postcode' | 'province' | 'country'
		>
	] = value;

	return payload;
}
